"use client"

import { ShoppingBag, Search, Menu, Bell, Home, User, BarChart3, Settings, Star } from "lucide-react"
import { FONTS, type DesignPreferences } from "@/lib/dream/design"
import type { Locale } from "@/lib/i18n"

// A live, code-drawn mock of the visitor's future product. It reacts instantly to the brand name,
// palette, typography, style and project type chosen in the studio (no AI involved).

type Theme = {
  primary: string
  accent: string
  bg: string
  surface: string
  text: string
  muted: string
  radius: number
  font: string
  headingFont: string
  uppercase: boolean
  border: string
}

const isDark = (hex: string) => {
  const h = hex.replace("#", "")
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
  return 0.299 * r + 0.587 * g + 0.114 * b < 128
}

function buildTheme(d: DesignPreferences): Theme {
  const [primary = "#034AA6", accent = "#38BDF8", bg0 = "#F8FAFC"] = d.colors ?? []
  let bg = bg0
  if (d.style === "tech" && !isDark(bg)) bg = "#0B1020"
  if (d.style === "luxury" && !d.colors) bg = "#111111"
  const dark = isDark(bg)
  const radius = { minimal: 14, corporate: 6, luxury: 2, playful: 22, tech: 12, natural: 18, bold: 4, retro: 4 }[d.style ?? ""] ?? 10
  const body = FONTS.find((f) => f.id === d.font)?.css ?? "ui-sans-serif, system-ui, sans-serif"
  return {
    primary,
    accent,
    bg,
    surface: dark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
    text: dark ? "#F8FAFC" : "#0F172A",
    muted: dark ? "rgba(248,250,252,0.6)" : "#64748B",
    radius,
    font: d.font === "serif" || d.font === "display" ? "ui-sans-serif, system-ui, sans-serif" : body,
    headingFont: body,
    uppercase: d.style === "bold" || d.font === "display",
    border: d.style === "retro" ? `2px solid ${dark ? "#F8FAFC" : "#0F172A"}` : `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`,
  }
}

const copy = {
  tr: {
    brand: "Markanız",
    nav: ["Ana Sayfa", "Hizmetler", "Hakkımızda", "İletişim"],
    cta: "Hemen Başla",
    sub: "Fikrinizi anlatın, önizleme burada canlanacak.",
    headline: { website: "Markanızın dijital yüzü", ecommerce: "Yeni sezon ürünleri", mobile: "Her şey cebinizde", "web-app": "Tüm verileriniz tek ekranda", automation: "İş akışlarınız otomatik", desktop: "Masaüstünde güçlü deneyim" } as Record<string, string>,
    features: ["Hızlı", "Güvenli", "Mobil uyumlu"],
    products: ["Ürün", "Yeni", "Popüler", "İndirim"],
    kpis: ["Ziyaret", "Satış", "Dönüşüm"],
    tabs: ["Keşfet", "Favoriler", "Profil"],
  },
  en: {
    brand: "Your Brand",
    nav: ["Home", "Services", "About", "Contact"],
    cta: "Get Started",
    sub: "Describe your idea and watch this preview come alive.",
    headline: { website: "Your brand, online", ecommerce: "New season collection", mobile: "Everything in your pocket", "web-app": "All your data on one screen", automation: "Your workflows on autopilot", desktop: "A powerful desktop experience" } as Record<string, string>,
    features: ["Fast", "Secure", "Responsive"],
    products: ["Product", "New", "Popular", "Sale"],
    kpis: ["Visits", "Sales", "Conversion"],
    tabs: ["Explore", "Favourites", "Profile"],
  },
}

// First sentence of the description, short enough for a hero headline
function headlineFrom(description: string, fallback: string) {
  const first = description.trim().split(/[.!?\n]/)[0]?.trim() ?? ""
  if (first.length < 8) return fallback
  return first.length > 52 ? `${first.slice(0, 50).replace(/\s+\S*$/, "")}…` : first
}

const slug = (s: string) =>
  s.toLowerCase().replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u").replace(/[^a-z0-9]+/g, "").slice(0, 18) || "markaniz"

type Props = { design: DesignPreferences; description: string; locale: Locale; device: "desktop" | "mobile" }

export function LivePreview({ design, description, locale, device }: Props) {
  const t = copy[locale]
  const th = buildTheme(design)
  const type = design.projectType ?? "website"
  const brand = design.brandName?.trim() || t.brand
  const headline = headlineFrom(description, t.headline[type] ?? t.headline.website)
  const heading = { fontFamily: th.headingFont, textTransform: th.uppercase ? "uppercase" : "none", letterSpacing: th.uppercase ? "0.02em" : undefined } as const
  const button = { background: th.primary, color: isDark(th.primary) ? "#fff" : "#0F172A", borderRadius: th.radius, fontFamily: th.font }
  const card = { background: th.surface, border: th.border, borderRadius: th.radius }
  const transition = "transition-all duration-500"

  const logo = (
    <span className="flex items-center gap-1.5 font-bold" style={{ ...heading, color: th.text }}>
      <span className={`inline-block h-4 w-4 ${transition}`} style={{ background: th.primary, borderRadius: Math.min(th.radius, 8) }} />
      <span className="truncate">{brand}</span>
    </span>
  )

  const isAppLayout = type === "web-app" || type === "automation" || type === "desktop"

  if (device === "mobile") {
    return (
      <div className="mx-auto w-[260px] rounded-[38px] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
        <div className={`relative h-[520px] overflow-hidden rounded-[28px] ${transition}`} style={{ background: th.bg, fontFamily: th.font, color: th.text }}>
          <div className="flex items-center justify-between px-4 pb-2 pt-3 text-[10px] font-semibold" style={{ color: th.muted }}>
            <span>9:41</span>
            <span className="h-4 w-16 rounded-full bg-slate-900" />
            <span>100%</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            {logo}
            {type === "ecommerce" ? <ShoppingBag className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          </div>
          <div className="px-4">
            <div className={`p-4 ${transition}`} style={{ ...card, background: `linear-gradient(135deg, ${th.primary}, ${th.accent})`, border: "none" }}>
              <p className="text-[15px] font-bold leading-snug text-white" style={heading}>{headline}</p>
              <span className="mt-3 inline-block px-3 py-1.5 text-[11px] font-semibold" style={{ ...button, background: "#fff", color: th.primary }}>{t.cta}</span>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 px-4">
            {t.products.map((p, i) => (
              <div key={p} className={`p-2 ${transition}`} style={card}>
                <div className="mb-2 h-14" style={{ background: i % 2 ? th.accent : th.primary, opacity: 0.18, borderRadius: Math.max(th.radius - 4, 2) }} />
                <p className="text-[11px] font-semibold">{p}</p>
                <p className="text-[10px]" style={{ color: th.muted }}>{type === "ecommerce" ? `₺${(i + 2) * 149}` : t.features[i % 3]}</p>
              </div>
            ))}
          </div>
          <div className="absolute inset-x-0 bottom-0 flex justify-around border-t px-2 py-3 text-[10px]" style={{ background: th.surface, borderColor: "rgba(148,163,184,0.25)", color: th.muted }}>
            {[Home, Search, User].map((Icon, i) => (
              <span key={i} className="flex flex-col items-center gap-0.5" style={{ color: i === 0 ? th.primary : undefined }}>
                <Icon className="h-4 w-4" />
                {t.tabs[i]}
              </span>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[860px] overflow-hidden rounded-xl border border-slate-300/70 bg-white shadow-2xl">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        <span className="ml-3 flex-1 truncate rounded-md bg-white px-3 py-1 font-mono text-[11px] text-slate-500">{slug(brand)}.com</span>
      </div>

      <div className={`min-h-[420px] ${transition}`} style={{ background: th.bg, fontFamily: th.font, color: th.text }}>
        {isAppLayout ? (
          <div className="flex min-h-[420px]">
            <aside className="hidden w-40 shrink-0 flex-col gap-1 p-3 sm:flex" style={{ background: th.surface, borderRight: th.border }}>
              <div className="mb-3 text-sm">{logo}</div>
              {[BarChart3, Home, User, Settings].map((Icon, i) => (
                <span key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs" style={{ borderRadius: th.radius, background: i === 0 ? `${th.primary}22` : undefined, color: i === 0 ? th.primary : th.muted }}>
                  <Icon className="h-3.5 w-3.5" /> {t.nav[i]}
                </span>
              ))}
            </aside>
            <div className="flex-1 p-4">
              <p className="text-lg font-bold" style={heading}>{headline}</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {t.kpis.map((k, i) => (
                  <div key={k} className={`p-3 ${transition}`} style={card}>
                    <p className="text-[10px]" style={{ color: th.muted }}>{k}</p>
                    <p className="text-lg font-bold" style={{ color: i === 0 ? th.primary : th.text }}>{["12.4K", "₺84K", "%3,8"][i]}</p>
                  </div>
                ))}
              </div>
              <div className={`mt-3 flex h-36 items-end gap-2 p-3 ${transition}`} style={card}>
                {[40, 65, 45, 80, 60, 95, 70, 85].map((h, i) => (
                  <span key={i} className={`flex-1 ${transition}`} style={{ height: `${h}%`, background: i % 2 ? th.accent : th.primary, borderRadius: Math.min(th.radius, 6) }} />
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-xs" style={card}>
                    <span>{t.products[i]} #{1024 + i}</span>
                    <span className="px-2 py-0.5 text-[10px]" style={{ background: `${th.accent}33`, color: th.text, borderRadius: th.radius }}>{t.features[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <nav className="flex items-center justify-between px-6 py-4 text-sm" style={{ borderBottom: th.border }}>
              {logo}
              <span className="hidden items-center gap-5 text-xs md:flex" style={{ color: th.muted }}>
                {t.nav.map((n) => <span key={n}>{n}</span>)}
              </span>
              {type === "ecommerce" ? <ShoppingBag className="h-4 w-4" /> : <Menu className="h-4 w-4 md:hidden" />}
            </nav>
            <section className="grid items-center gap-6 px-6 py-8 md:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: th.accent }}>{brand}</p>
                <h3 className={`mt-2 text-2xl font-bold leading-tight md:text-3xl ${transition}`} style={heading}>{headline}</h3>
                <p className="mt-3 text-sm" style={{ color: th.muted }}>{t.sub}</p>
                <span className={`mt-5 inline-block px-5 py-2.5 text-sm font-semibold ${transition}`} style={button}>{t.cta}</span>
              </div>
              <div className={`h-44 ${transition}`} style={{ borderRadius: th.radius * 1.5, background: `linear-gradient(135deg, ${th.primary}, ${th.accent})` }}>
                <div className="flex h-full items-center justify-center">
                  <Star className="h-10 w-10 text-white/80" />
                </div>
              </div>
            </section>
            <section className="grid grid-cols-2 gap-3 px-6 pb-8 md:grid-cols-4">
              {(type === "ecommerce" ? t.products : [...t.features, t.nav[1]]).map((p, i) => (
                <div key={p + i} className={`p-3 ${transition}`} style={card}>
                  <div className="mb-2 h-16" style={{ background: i % 2 ? th.accent : th.primary, opacity: 0.2, borderRadius: Math.max(th.radius - 4, 2) }} />
                  <p className="text-xs font-semibold">{p}</p>
                  <p className="text-[11px]" style={{ color: th.muted }}>{type === "ecommerce" ? `₺${(i + 2) * 149}` : "Lorem ipsum"}</p>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
